import React from "react";
import styled from "styled-components";
import FormSelectPagedCreatable from "../FormSelectPagedCreatable";
import { darken } from "polished";
import { useLazyFetchUserEmailContactsQuery } from "../../../services/useremails";
import { components, type MultiValue, type SingleValue, type OptionProps, type Props, type GroupBase } from "react-select";

export type EmailContactsSelectOption = SelectOptionData<string, EmailContactRecord>;
export type EmailContactRecord = {
    userId: string | null,
    masterAccountId: string | null,
    contactName: string,
    contactEmailAddress: string,
    contactType: number,
    addressBookType: number,
    createdDate: string,
    isRestricted: boolean,
    isActive: boolean,
    isActiveDetails: boolean 
};

export type EmailContactsPagedSelectProps<Option, IsMulti extends boolean, Group extends GroupBase<Option>> = Props<Option, IsMulti, Group> & {
    masterAccountIds?: string[] | null
    userId: string
}

const EmailContactsSelectOptionSubTitle = styled.div`
    font-size: 0.9em;
    color: ${(props: any) => props.isSelected === true ? props.theme.light : props.theme.muted};
`;

const EmailContactsSelectMultiValueLabel = styled.span`
    &:hover {
        background-color: ${props => darken(props.theme.linkDarkenAmount * 1.75, props.theme.light)};
    }
`;

const EmailContactsSelectOption = (props: OptionProps<EmailContactsSelectOption, boolean, any>) => {
    const { data: { data } } = props;

    if (!data) {
        return (
            <components.Option {...props}>
                <span>{props.label}</span>
            </components.Option>
        )
    }

    const { contactName, contactEmailAddress, contactType } = data;
    return (
        <components.Option {...props}>
            <span>{contactName}</span>
            <EmailContactsSelectOptionSubTitle className="row font-monospace" isSelected={props.isSelected}>
                <div className="col-6 text-truncate">
                    {contactEmailAddress}
                </div>
                <div className="col-6 text-truncate">
                    {contactType === 0 ? 'Personal Address Book' : 'Master Account Address Book'}
                </div>
            </EmailContactsSelectOptionSubTitle>
        </components.Option>
    );
};

const EmailContactsMultiValueLabel = (props: OptionProps<EmailContactsSelectOption, boolean, any>) => {
    const { data: { data } } = props;

    const handleOnClickEvent = (e: any) : void => {

    };

    if (!data || data.contactType !== 0) {
        return (
            <components.MultiValueLabel {...props}>
                {props.children}
            </components.MultiValueLabel>
        )
    }

    return (
        <components.MultiValueLabel {...props}>
            <EmailContactsSelectMultiValueLabel className="has-pointer" onClick={handleOnClickEvent}>
                {props.children}
            </EmailContactsSelectMultiValueLabel>
        </components.MultiValueLabel>
    )
};

function EmailContactsPagedSelect<Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>({
    masterAccountIds = null,
    userId,
    ...rest
} : EmailContactsPagedSelectProps<Option, IsMulti, Group>) {

    const onOptionsLoaded = (results: any) => {
        return results;
    }

    return <FormSelectPagedCreatable
        query={useLazyFetchUserEmailContactsQuery}
        queryParams={{ masterAccountIds }}
        additional={{ userId, page: 1, pageLimit: 25 }}
        isCreatable
        isMulti={true}
        components={{
            Option: EmailContactsSelectOption,
            MultiValueLabel: EmailContactsMultiValueLabel
        }}
        onOptionsLoaded={(_: any, results: any) => onOptionsLoaded(results)}
        {...rest}
    />;
}

export default EmailContactsPagedSelect;